<template>
  <div class="custom-photo-word-wrap" id="custom-photo-word-wrap">
    <div class="custom-photo-word__input-wrap">
      <sib-input
        class="custom-photo-word__input"
        size="md"
        round="md"
        :placeholder="customizationTemplate.placeholder || 'Type here...'"
        :maxlength="Number(customizationTemplate.maxlength || MAX_LENGTH)"
        v-model="word"
      ></sib-input>
      <div
        class="custom-photo-word__input-length"
        v-if="customizationTemplate.maxlength"
      >
        {{ currentWordLength }}
      </div>
    </div>

    <div class="custom-photo-word__image-wrap">
      <div
        class="custom-photto-word__image"
        ref="customPhotoWord"
        :style="{
          position: 'absolute',
          width: `${WORD_IMAGE_SIZE}px`,
          zIndex: -5000,
          left: '-5000px',
        }"
      >
        <div
          v-if="word"
          class="custom-photo-word"
          :style="{ gap: customizationTemplate.letterMargin || '0px' }"
        >
          <template
            v-for="(letter, index) in word?.toUpperCase()?.split('')"
            :key="index"
          >
            <div
              v-if="lettersImage[letter.toUpperCase()]"
              class="custom-photo-word__letter"
            >
              <img
                :style="{
                  height: letterHeight,
                  maxWidth: letterMaxWidth,
                  width: '100%',
                }"
                :src="lettersImage[letter.toUpperCase()]"
                :alt="index"
              />
            </div>
            <div
              v-else-if="letter == ' '"
              :style="{
                padding: customizationTemplate.letterSpaceMargin || '20px',
              }"
            ></div>
            <div
              v-else
              class="custom-photo-word__letter"
              :style="{
                fontSize: letterHeight,
              }"
            >
              {{ letter }}
            </div>
          </template>
        </div>
        <div v-else class="custom-photo-word--no-word"></div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import html2canvas from 'html2canvas'
import { ref, watch, onMounted, computed, onBeforeMount } from 'vue'
import { cloneDeep, debounce, QUERY_STRING_NO_CACHE } from '@/utils'
import Provider from '@/provider'
import { useProductCustomization } from './composables/ProductCustomization'
import useProductStore from '@/store/product'
import CacheService, { getJSONfromCacheResponse } from '@/services/cache'
import { useRoute } from 'vue-router'

const props = withDefaults(
  defineProps<{
    inputFileCustomily: Element
    customizationTemplate: {
      customilyLibraryId?: string
      maxlength?: string | number
      placeholder?: string
      letterHeight?: string
      letterWidth?: string
      letterMargin?: string
      letterSpaceMargin?: string
      bestLengthLetters?: string | number
      letterImageRatio: string | number
    }
    customilyOptionWrapper: HTMLElement
    optionSortId: any
  }>(),
  {}
)

const route = useRoute()
const productStore = useProductStore()
const { convertImageLinkToFileList, changeFileImageCustomily } =
  useProductCustomization()
const cacheKeyLettersImage = `customPhotoWord_lettersImage_${props.customizationTemplate.customilyLibraryId}`

const MAX_LENGTH = 500
const imageLink = ref('')
const word = ref('')
const customPhotoWord = ref(null)
const lettersImage = ref({})
const LETTERS = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
  'O',
  'P',
  'Q',
  'R',
  'S',
  'T',
  'U',
  'V',
  'W',
  'X',
  'Y',
  'Z',
]
const WORD_IMAGE_SIZE = 500 // 500px
const letterMaxWidth = computed(() => {
  if (props?.customizationTemplate?.letterWidth) {
    const numberLetterWidth =
      props?.customizationTemplate?.letterWidth?.split('px')[0]
    return `${WORD_IMAGE_SIZE / numberLetterWidth}px`
  } else if (props?.customizationTemplate?.bestLengthLetters) {
    return `${
      WORD_IMAGE_SIZE / props.customizationTemplate.bestLengthLetters
    }px`
  } else if (props?.customizationTemplate?.maxlength) {
    return `${WORD_IMAGE_SIZE / props.customizationTemplate.maxlength}px`
  }
  return '100px'
})

const letterHeight = computed(() => {
  const letterMaxWidthNumber = letterMaxWidth.value?.split('px')[0]
  if (props?.customizationTemplate?.letterHeight) {
    return props.customizationTemplate.letterHeight
  } else if (props?.customizationTemplate?.letterImageRatio) {
    return `${
      letterMaxWidthNumber / props.customizationTemplate.letterImageRatio
    }px`
  } else {
    return letterMaxWidth.value
  }
})

const currentWordLength = computed(() => {
  return `${word?.value?.split('')?.length || 0}/${Number(
    props?.customizationTemplate.maxlength
  )}`
})

onBeforeMount(() => {
  getLettersImage()
})

onMounted(async () => {
  if (productStore.currentWordCustomPhotoWordProduct[props.optionSortId]) {
    word.value =
      productStore.currentWordCustomPhotoWordProduct[props.optionSortId]
    const wordInput = props.customilyOptionWrapper.querySelector(
      '.custom-photo-word__input'
    )
    if (wordInput) {
      wordInput.value = word.value
      const event = new Event('input')
      wordInput.dispatchEvent(event)
    }
  }

  const typeWordDebounce = debounce(handleChangeWord, 500)
  watch(word, typeWordDebounce)
})

async function handleChangeWord() {
  try {
    productStore.currentWordCustomPhotoWordProduct[props.optionSortId] =
      word.value
    await captureImage()
  } catch (error) {}
}

async function captureImage() {
  if (!customPhotoWord.value) return
  html2canvas(customPhotoWord.value, {
    useCORS: true,
    allowTaint: false,
    backgroundColor: null,
  }).then(async (canvas: any) => {
    const link = document.createElement('a')
    link.href = canvas.toDataURL('image/png')
    imageLink.value = canvas.toDataURL('image/png')
    const imageFileList = await convertImageLinkToFileList(
      link.href,
      'photo-word'
    )
    changeFileImageCustomily(imageFileList, props.inputFileCustomily)
  })
}

async function getLettersImage() {
  try {
    const isForceLoadNewData = !!route.query[QUERY_STRING_NO_CACHE]
    if (cacheKeyLettersImage && !isForceLoadNewData) {
      const cacheResponse = await CacheService.instance?.get(
        cacheKeyLettersImage
      )
      const lettersImageFromCache = getJSONfromCacheResponse(cacheResponse)
      if (lettersImageFromCache) {
        lettersImage.value = lettersImageFromCache
        return
      }
    }

    const libraryId = props.customizationTemplate.customilyLibraryId
    if (!libraryId) return
    const provider = await Provider.getInstance()
    const lettersPosition = await provider.getLettersPositionCustomilyLibrary(
      libraryId
    )
    if (lettersPosition?.length) {
      let letterImageCustomily = await Promise.all(
        lettersPosition.map((letterPosition) =>
          provider.getLetterImageCustomilyLibrary(libraryId, letterPosition)
        )
      )
      letterImageCustomily = letterImageCustomily.sort(
        (a, b) => a.sort - b.sort
      )

      for (let i = 0; i < letterImageCustomily.length; i++) {
        const key = LETTERS[letterImageCustomily[i].sort - 1]
        lettersImage.value[key] = letterImageCustomily[i].image
      }

      if (Object.getOwnPropertyNames(lettersImage.value)?.length) {
        CacheService.instance?.set(
          cacheKeyLettersImage,
          cloneDeep(lettersImage.value)
        )
      }
    }
  } catch (error) {}
}
</script>

<style lang="scss">
.custom-photo-word {
  display: flex;
  justify-content: center;

  &__input {
    border: 1px solid #d5d5d5 !important;
    padding-left: 15px !important;
    line-height: 25px;
  }

  &--no-word {
    position: relative;
    width: 1px;
    height: 1px;
    z-index: -5;
    left: -5000px;
    opacity: 0;
  }

  &__image-wrap {
    position: relative;
    opacity: 0;
  }
  &__letter {
    line-height: 1;
  }
  &__input-wrap {
    position: relative;
  }
  &__input-length {
    position: absolute;
    right: 15px;
    top: 15px;
  }
}
</style>
